'use-client';

import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/lib/foundations/controls/button';
import { ProgressBar } from '@/lib/foundations/disclosures/progress-bar';
import { IoPauseSharp, IoPlaySharp } from 'react-icons/io5';
import { PrimaryButton } from '@/components/foundations/primary-button';
import { TxmyIcon } from '@/components/foundations/txmy-icon';
import Link from 'next/link';

/* ================================== TYPES ================================= */

export type TUrlAudioPlayerProps = {
  url?: string; // S3 URL for the .wav file
  albumArt?: string; // URL for album art
  trackTitle?: string; // Track title
  subtitle?: string; // Subtitle
  tags?: string[]; // Array of tags
  shareCode?: string; // Share code
};

/* ========================================================================== */
/*                                  COMPONENT                                 */
/* ========================================================================== */
export function UrlAudioPlayer(props: TUrlAudioPlayerProps) {
  const { url, albumArt, trackTitle, subtitle, tags, shareCode } = props;

  const audioRef = useRef<HTMLAudioElement | null>(null); // Reference to the audio element
  const [isPlaying, setIsPlaying] = useState(false); // To track play/pause state
  const [duration, setDuration] = useState<number | null>(null); // Total duration of the audio
  const [currentTime, setCurrentTime] = useState<number>(0); // Current playback time
  const [copyMessage, setCopyMessage] = useState<string | null>(null); // New state for copy message

  // Toggle play/pause state
  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const fileExtension = url?.split('?')[0].split('.').pop();
  const downloadName = `${trackTitle || 'Unknown Title'}.${fileExtension}`;

  // Update currentTime when audio plays
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  // Set duration when metadata is loaded
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  // Reset when playback ends
  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);

      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);

      return () => {
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('pause', handlePause);
      };
    }
  }, [url]);

  // Auto-play when a new URL is available
  useEffect(() => {
    if (audioRef.current && url) {
      audioRef.current.play();
    }
  }, [url]);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(
        `https://infinity.lifescoremusic.com/txmy/ethereal/${shareCode}` || '',
      );
      setCopyMessage('URL copied to clipboard!');
      setTimeout(() => setCopyMessage(null), 3000); // Hide message after 3 seconds
    } catch (error) {
      setCopyMessage('Failed to copy URL.');
      setTimeout(() => setCopyMessage(null), 3000);
    }
  };

  // Function to download the file using fetch and Blob
  const handleDownload = async () => {
    if (!url) return;

    try {
      const response = await fetch(url);
      console.log('fetching file', url);
      if (!response.ok) throw new Error('Network response was not ok');

      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = downloadName;
      document.body.appendChild(link); // Append to body to make it work in Firefox
      link.click();
      document.body.removeChild(link); // Remove the link after triggering download
      URL.revokeObjectURL(link.href); // Clean up
    } catch (error) {
      console.error('Failed to download file:', error);
    }
  };

  return (
    <div
      style={{
        width: '320px',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: '#f0f0f0',
        boxShadow: '0 10px 10px 10px rgba(0, 0, 0, 0.3)',
      }}
    >
      <div
        style={{
          position: 'relative',
          height: '150px',
          background: albumArt ? `url(${albumArt}) center/cover` : '#ccc',
        }}
      >
        <div
          style={{
            position: 'absolute',
            bottom: '15px',
            left: '15px',
          }}
        >
          <Button
            size="lg"
            radius="full"
            onPress={togglePlayPause}
            isIconOnly
            isDisabled={url === undefined}
            color="primary"
            variant="solid"
          >
            <span
              className={
                !isPlaying ? 'text-2xl relative left-[2px]' : 'text-2xl'
              }
            >
              {isPlaying ? <IoPauseSharp /> : <IoPlaySharp />}
            </span>
          </Button>
        </div>
      </div>

      {/* Audio Element */}
      {url && (
        <audio
          ref={audioRef}
          src={url}
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleAudioEnded}
        />
      )}

      {/* Progress Bar */}
      {duration && (
        <ProgressBar
          value={(currentTime / duration) * 100}
          aria-label="playing"
          style={{ height: '4px', backgroundColor: '#6200ea' }}
        />
      )}

      {/* Audio Info */}
      <div style={{ padding: '10px' }}>
        <div className="font-meyer text-4xl pt-2 mb-0">
          {trackTitle || 'Unknown Title'}
        </div>
        <div className="mt-0 text-sm text-gray-700">
          {subtitle || 'Unknown Artist'}
        </div>
        <div className="mt-3 text-xs text-gray-500">
          {tags && tags.length > 0 ? tags.join(' / ') : 'No Tags'}
        </div>
        <div className="mt-4 text-xs text-gray-600 p-2">
          Download your remix, submit it by using the audio in a short-form post
          on either TikTok or Instagram Reels, tag @lifescore_infinity, and
          write your [unique remix code] in the post caption.
        </div>
        <div className="p-4 w-full flex justify-end items-center">
          <Button onPress={handleDownload} variant="bordered">
            <TxmyIcon type="save" className="text-xl" /> Download
          </Button>
          <Button onPress={handleCopyUrl} variant="bordered">
            <TxmyIcon type="send" className="text-xl" /> Share
          </Button>
          {copyMessage && (
            <div className="ml-2 text-xs text-gray-600">{copyMessage}</div>
          )}
        </div>
      </div>
    </div>
  );
}
